import { Response } from "react-select-async-paginate";
import Client from "../../models/client";
import AsyncPaginateBootstrap from "../react-select-bootstrap/AsyncPaginateBootstrap";
import { getList } from "../../api/generics";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useState } from "react";
import { createPortal } from "react-dom";
import { components } from "react-select";
import ClientUpdateModal from "./ClientUpdateModal";
import HasAnyPermissionComponent from "../permissions/HasAnyPermissionComponent";
import {
  INVOICES_CREATE,
  INVOICES_UPDATE,
  CREDIT_NOTES_CREATE,
  CREDIT_NOTES_UPDATE,
  DEBIT_NOTES_CREATE,
  DEBIT_NOTES_UPDATE,
  REMISSION_NOTES_CREATE,
  REMISSION_NOTES_UPDATE,
  RECEIPTS_CREATE,
  RECEIPTS_UPDATE,
} from "../../auth/permissions";
import "./client-select.scss";

const RECORS_PER_SEARCH = 20;

type ClientSelectProps = {
  value: Client | null;
  onChange: (_: Client | null) => void;
  editable?: boolean;
};

const ClientSelect: React.FC<ClientSelectProps> = ({
  value,
  onChange,
  editable = false,
}) => {
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [optionsVersion, setOptionsVersion] = useState(0);
  const taxPayerId = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer.id
  );
  const loadClientOptions = async (
    s: string,
    currentOpts: readonly (Client | { options: Client[]; label?: string })[]
  ): Promise<Response<Client, { options: Client[]; label?: string }, any>> => {
    let additionalParams: Map<string, string> | undefined = new Map<
      string,
      string
    >();

    additionalParams.set("tax_payer_id", taxPayerId!.toString());

    if (s !== "") {
      additionalParams.set("search", s);
    }

    const clientsPaginated = await getList<Client>(
      "/clients/",
      RECORS_PER_SEARCH,
      currentOpts.length,
      additionalParams
    );

    let newOpts: Client[] = [];
    let count = 0;

    if (clientsPaginated.data !== undefined) {
      newOpts = clientsPaginated.data.items;
      count = clientsPaginated.data.count;
    }

    const hasMore = currentOpts.length + newOpts.length < count;

    return {
      options: newOpts,
      hasMore: hasMore,
    };
  };
  return (
    <>
      <AsyncPaginateBootstrap
        placeholder={"Seleccione un Cliente"}
        onChange={onChange}
        isMulti={false}
        debounceTimeout={300}
        loadOptions={loadClientOptions}
        cacheUniqs={[optionsVersion]}
        components={{
          IndicatorsContainer: (props) => (
            <components.IndicatorsContainer {...props}>
              {editable && value && !props.isDisabled && (
                <HasAnyPermissionComponent
                  permissionNames={[
                    INVOICES_CREATE,
                    INVOICES_UPDATE,
                    CREDIT_NOTES_CREATE,
                    CREDIT_NOTES_UPDATE,
                    DEBIT_NOTES_CREATE,
                    DEBIT_NOTES_UPDATE,
                    REMISSION_NOTES_CREATE,
                    REMISSION_NOTES_UPDATE,
                    RECEIPTS_CREATE,
                    RECEIPTS_UPDATE,
                  ]}
                >
                  <button
                    type="button"
                    className="client-select-edit"
                    title="Editar cliente"
                    aria-label="Editar cliente"
                    onMouseDown={(event) => event.stopPropagation()}
                    onTouchEnd={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      setEditingClient({ ...value });
                    }}
                  >
                    <i className="fa fa-pencil" aria-hidden="true" />
                  </button>
                </HasAnyPermissionComponent>
              )}
              {props.children}
            </components.IndicatorsContainer>
          ),
        }}
        getOptionLabel={(option: Client) =>
          [option.ruc, option.socialName || option.fantasyName]
            .filter(Boolean)
            .join(" - ") || "Cliente del documento"
        }
        getOptionValue={(option: Client) =>
          option.id != null
            ? String(option.id)
            : `document:${option.ruc ?? ""}:${option.socialName ?? option.fantasyName ?? ""}`
        }
        value={value}
      />
      {editingClient &&
        createPortal(
          <ClientUpdateModal
            show
            client={editingClient}
            onCancel={() => setEditingClient(null)}
            onSuccess={(updatedClient) => {
              if (updatedClient) {
                onChange(updatedClient);
                setOptionsVersion((version) => version + 1);
              }
              setEditingClient(null);
            }}
          />,
          document.body,
        )}
    </>
  );
};

export default ClientSelect;
